import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  FormattedMessage,
  defineMessages,
  useIntl,
  injectIntl
} from "react-intl";
import styled from "styled-components";
import DatePicker from "react-datepicker";
import DateTimePickerStyles from "./DateTimePicker";
import { Icon, Dropdown, BREAKPOINTS } from "@onelogin/react-components";
import ResponsiveTable from "../common/Table/ResponsiveTable";
import {
  NameColumn,
  AccessColumn,
  RemoveColumn,
  ExpiryColumn
} from "./GrantsColumn";

const ICON_IMAGE_SIZE = 24;
const ICON_MARGIN = 16;

const messages = defineMessages({
  deleteNote: {
    id: "deleteNote",
    defaultMessage: "Delete note"
  },
  accessDropdown: {
    id: "accessDropdown",
    defaultMessage: "Access Dropdown"
  }
});

const DateTimePickerWrapper = styled.div`
  ${DateTimePickerStyles}
`;

const IconWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 0 0 ${ICON_IMAGE_SIZE + ICON_MARGIN}px;
  width: 44px;
  height: 44px;

  @media screen and (max-width: ${BREAKPOINTS.PHONE_LARGE}) {
    justify-content: left;
  }
`;

const GrantsTableContainer = styled.div`
  text-align: left;
  @media screen and (max-width: ${BREAKPOINTS.PHONE_LARGE}) {
    margin-bottom: 64px;
  }
`;

const TrashcanIcon = styled(Icon).attrs({
  icon: "Trashcan-24",
  size: "24"
})`
  cursor: pointer;
`;

const ExpiresLabelWrapper = styled.div`
  padding-right: 5px;
  line-height: 35px;
`;

const getTableStructure = () => {
  return {
    columns: [
      {
        minWidth: 40,
        weight: 25,
        priority: 1,
        content: <FormattedMessage defaultMessage="Name" id="name" />,
        key: "name"
      },
      {
        minWidth: 25,
        weight: 40,
        priority: 1,
        content: "",
        key: "access"
      },
      {
        minWidth: 40,
        weight: 40,
        priority: 1,
        content: "",
        key: "expireAt"
      },
      {
        minWidth: 25,
        weight: 15,
        priority: 1,
        content: "",
        key: "remove"
      }
    ].filter(item => item !== undefined)
  };
};

const GrantsTable = props => {
  const intl = useIntl();
  const [tableStructure] = useState(getTableStructure(props));
  tableStructure.data = [];

  if (props.userGranted) {
    for (let [index, item] of props.userGranted.entries()) {
      const editorOption = {
        text: intl.formatMessage({ id: "editor", defaultMessage: "Editor" }),
        value: { writeAccess: true, userId: item.userId },
        id: "user-editor-" + item.userId
      };
      const viewerOption = {
        text: intl.formatMessage({ id: "viewer", defaultMessage: "Viewer" }),
        value: { writeAccess: false, userId: item.userId },
        id: "user-viewer-" + item.userId
      };
      const accessOption = [editorOption, viewerOption];

      const name = (
        <NameColumn>
          <span title={item.fullName}>{item.fullName}</span>
        </NameColumn>
      );

      const access = (
        <AccessColumn>
          <Dropdown
            id={`user-access-${item.userId}`}
            list={accessOption}
            value={(item.writeAccess ? editorOption : viewerOption).value}
            onChange={props.onSelectUserAccess}
            ariaLabel={props.intl.formatMessage(messages.accessDropdown)}
          />
        </AccessColumn>
      );

      const expireAt = (
        <ExpiryColumn>
          <ExpiresLabelWrapper>
            <div>{"Expires"}</div>
          </ExpiresLabelWrapper>
          <DateTimePickerWrapper>
            <DatePicker
              id={`user-expireAt-${item.userId}`}
              selected={
                item?.expireAt === undefined
                  ? new Date()
                  : item?.expireAt === null
                  ? null
                  : new Date(item.expireAt)
              }
              onChange={expiryDate =>
                props.onSelectUserExpiry(item, expiryDate || null)
              }
              isClearable
              onClick={event => event.stopPropagation()}
              minDate={new Date()}
              dateFormat="yyyy-MM-dd"
            />
          </DateTimePickerWrapper>
        </ExpiryColumn>
      );

      const remove = (
        <RemoveColumn>
          <IconWrapper
            data-testid="user-remove-icon"
            onClick={() => props.onRemoveUser(item)}
          >
            <TrashcanIcon
              data-testid={`delete-user-${item.userId}-button`}
              alt={intl.formatMessage(messages.deleteNote)}
            />
          </IconWrapper>
        </RemoveColumn>
      );

      const cells = [
        { key: "name", content: name },
        { key: "access", content: access },
        { key: "expireAt", content: expireAt },
        { key: "remove", content: remove }
      ].filter(item => item !== undefined);

      tableStructure.data.push({
        cells,
        meta: index === 0 ? { section: "people" } : null
      });
    }
  }

  if (props.roleGranted) {
    for (let [index, item] of props.roleGranted.entries()) {
      const editorOption = {
        text: intl.formatMessage({ id: "editor", defaultMessage: "Editor" }),
        value: { writeAccess: true, roleId: item.roleId },
        id: "role-editor-" + item.roleId
      };
      const viewerOption = {
        text: intl.formatMessage({ id: "viewer", defaultMessage: "Viewer" }),
        value: { writeAccess: false, roleId: item.roleId },
        id: "role-viewer-" + item.roleId
      };
      const accessOption = [editorOption, viewerOption];

      const name = <NameColumn>{item.roleName}</NameColumn>;

      const access = (
        <AccessColumn>
          <Dropdown
            id={`role-access-${item.roleId}`}
            list={accessOption}
            value={(item.writeAccess ? editorOption : viewerOption).value}
            onChange={props.onSelectRoleAccess}
            ariaLabel={props.intl.formatMessage(messages.accessDropdown)}
          />
        </AccessColumn>
      );

      const expireAt = (
        <ExpiryColumn>
          <ExpiresLabelWrapper>
            <div>{"Expires"}</div>
          </ExpiresLabelWrapper>
          <DateTimePickerWrapper>
            <DatePicker
              id={`role-expireAt-${item.roleId}`}
              selected={
                item?.expireAt === undefined
                  ? new Date()
                  : item?.expireAt === null
                  ? null
                  : new Date(item.expireAt)
              }
              onChange={expiryDate =>
                props.onSelectRoleExpiry(item, expiryDate || null)
              }
              isClearable
              onClick={event => event.stopPropagation()}
              minDate={new Date()}
              dateFormat="yyyy-MM-dd"
            />
          </DateTimePickerWrapper>
        </ExpiryColumn>
      );

      const remove = (
        <RemoveColumn>
          <IconWrapper
            onClick={() => props.onRemoveRole(item)}
            data-testid="role-remove-icon"
          >
            <TrashcanIcon
              data-testid={`delete-role-${item.roleId}-button`}
              alt={intl.formatMessage(messages.deleteNote)}
            />
          </IconWrapper>
        </RemoveColumn>
      );

      const cells = [
        { key: "name", content: name },
        { key: "access", content: access },
        { key: "expireAt", content: expireAt },
        { key: "remove", content: remove }
      ].filter(item => item !== undefined);

      tableStructure.data.push({
        cells,
        meta: index === 0 ? { section: "roles" } : null
      });
    }
  }

  return (
    <GrantsTableContainer>
      <ResponsiveTable
        tableStructure={tableStructure}
        showBorder={true}
        intl={intl}
        noDataMessage={
          <FormattedMessage
            id="noGrants"
            defaultMessage="This note has not been shared yet."
          />
        }
      />
    </GrantsTableContainer>
  );
};

GrantsTable.propTypes = {
  userGranted: PropTypes.array,
  roleGranted: PropTypes.array,
  onRemoveRole: PropTypes.func.isRequired,
  onRemoveUser: PropTypes.func.isRequired,
  onSelectUserAccess: PropTypes.func.isRequired,
  onSelectRoleAccess: PropTypes.func.isRequired
};

export default injectIntl(GrantsTable);
